import { FC } from 'react';
import { Grid, Box, Typography } from '@mui/material';
import { FieldRow, FieldLabel, Panel, Title } from 'src/generic/component';
import { BooleanRadio, TextArea, Input, Checkboxes } from 'src/generic/component/form';
import { CustomAddressFields } from 'src/containers/school/ai/components/CustomAddressFields';
import { IFieldErrors, ErrorManager } from 'src/types/validation';
import { Draft } from 'src/types/common';
import { IAIRequest, IExamSecurity } from 'src/types/org/ai/schoolInterface';
import { IAddress } from 'src/types/address';

interface IExamSecurityPanelProps {
  item: Draft<IAIRequest>;
  readOnly?: boolean;
  errors: IFieldErrors;
  onUpdate: (updates: Draft<IAIRequest>) => void;
}

export const ExamSecurityPanel: FC<IExamSecurityPanelProps> = ({
  item,
  readOnly,
  errors,
  onUpdate
}) => {
  const { getError, hasError, getFieldError } = ErrorManager(errors);

  const appForm = item.data?.appForm;
  const examSecurity = appForm?.examSecurity || {};
  const org = item.data?.org;

  const {
    heldAtVisitingAddress,
    alternativeTestingAddress,
    alternativeAddressExplanation,
    materialReceivedBy,
    materialReceivingArea,
    materialReceivingAreaOther,
    materialStorageLocation,
    isMaterialStorageLocked,
    securityResponsible,
    allowNonEmployedAccess,
    hasDesignatedTestingRooms,
    hasTestingRoomPowerWifi
  } = examSecurity;

  // Material receiving area options
  const RECEIVING_AREA_OPTIONS = [
    { key: 'Main Office', label: 'Main Office' },
    { key: 'Loading Dock', label: 'Loading Dock' },
    { key: 'Other', label: 'Other' }
  ];

  const changed = (updates: Partial<IExamSecurity>) => {
    const updatedItem: Draft<IAIRequest> = {
      ...item,
      data: {
        ...item.data,
        appForm: {
          ...appForm,
          examSecurity: {
            ...appForm?.examSecurity,
            ...updates
          }
        }
      }
    };
    onUpdate(updatedItem);
  };

  return (
    <Panel>
      <Title>Exam Security</Title>
      <Grid container spacing={5}>
        {/* Left Side Grid */}
        <Grid item xs={6}>
          <FieldRow>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={8}>
                <FieldLabel variant="body1">
                  Will testing be held at the physical address provided?
                </FieldLabel>
              </Grid>
              <Grid item xs={4}>
                <BooleanRadio
                  readOnly={readOnly}
                  error={getError('data.appForm.examSecurity.heldAtVisitingAddress')}
                  value={heldAtVisitingAddress}
                  name='heldAtVisitingAddress'
                  onChange={(value) => {
                    const updates: Partial<IExamSecurity> = { heldAtVisitingAddress: value };
                    // Clear alternative address fields if Yes is selected
                    if (value) {
                      updates.alternativeTestingAddress = undefined;
                      updates.alternativeAddressExplanation = undefined;
                    } else {
                      // Initialize alternative testing address with proper IAddress structure
                      const defaultAddress: IAddress = {
                        streetAddr1: '',
                        streetAddr2: '',
                        streetAddr3: '',
                        city: '',
                        countryIsoCd: org?.visitingAddress?.countryIsoCd || 'US',
                        stateCd: org?.visitingAddress?.stateCd || '',
                        province: org?.visitingAddress?.province || '',
                        intlPostalCd: '',
                        contactName: '',
                      };
                      updates.alternativeTestingAddress = defaultAddress;
                    }
                    changed(updates);
                  }}
                />
              </Grid>
            </Grid>
          </FieldRow>

          {/* Conditional alternative testing address and explanation */}
          {heldAtVisitingAddress === false && (
            <>
              {/* Alternative Testing Address Fields */}
              <FieldRow>
                <FieldLabel variant="body1" style={{ fontWeight: 'bold', marginBottom: '12px' }}>
                  Alternative Testing Address
                </FieldLabel>
                <CustomAddressFields 
                  readOnly={readOnly}
                  errors={getFieldError('data.appForm.examSecurity.alternativeTestingAddress')}
                  address={alternativeTestingAddress}
                  onUpdate={(address: Partial<IAddress>) => {
                    // Ensure the address has all required IAddress properties
                    const completeAddress: IAddress = {
                      streetAddr1: address.streetAddr1 || '',
                      streetAddr2: address.streetAddr2 || '',
                      streetAddr3: address.streetAddr3 || '',
                      city: address.city || '',
                      countryIsoCd: address.countryIsoCd || 'US',
                      stateCd: address.stateCd || '',
                      province: address.province || undefined,
                      intlPostalCd: address.intlPostalCd || undefined,
                      contactName: address.contactName || undefined,
                      contactPhone: address.contactPhone || undefined,
                      zip5: address.zip5 || undefined,
                      zip4: address.zip4 || undefined,
                    };
                    changed({ alternativeTestingAddress: completeAddress });
                  }}
                />
              </FieldRow>

              {/* Explanation Text Area */}
              <FieldRow>
                <FieldLabel variant="body1" style={{ marginBottom: '8px' }}>
                  Explanation
                </FieldLabel>
                <Typography
                  variant="caption"
                  component="div"
                  style={{
                    marginBottom: '8px',
                    color: '#666',
                    lineHeight: '1.4',
                    fontSize: '0.875rem'
                  }}
                >
                  College Board assessments must be shipped, stored and administered at the same location. Please explain the affiliation between your organization and this alternative testing location.
                </Typography>
                <TextArea
                  disabled={readOnly}
                  validation={hasError('data.appForm.examSecurity.alternativeAddressExplanation') ? 'error' : undefined}
                  validationMsg={getError('data.appForm.examSecurity.alternativeAddressExplanation')}
                  onChange={(e) => {
                    changed({ alternativeAddressExplanation: e.target.value });
                  }}
                  value={alternativeAddressExplanation || ''}
                />
              </FieldRow>
            </>
          )}

          {/* Who will be responsible for receiving materials? */}
          <FieldRow>
            <FieldLabel variant="body1">
              Who will be responsible for receiving College Board exam materials?
            </FieldLabel>
            <Input
              disabled={readOnly}
              value={materialReceivedBy || ''}
              validation={hasError('data.appForm.examSecurity.materialReceivedBy') ? 'error' : undefined}
              validationMsg={getError('data.appForm.examSecurity.materialReceivedBy')}
              onChange={(e) => {
                changed({ materialReceivedBy: e.target.value });
              }}
              placeholder="Enter name and title"
              maxLength={255}
            />
          </FieldRow>
        </Grid>

        {/* Right Side Grid */}
        <Grid item xs={6}>

          {/* Material receiving area checkboxes - Horizontal layout */}
          <FieldRow>
            <FieldLabel variant="body1">
              Where in the building will test materials be received?
            </FieldLabel>
            <Box sx={{
              marginTop: '8px',
              display: 'flex',
              flexDirection: 'row',
              gap: '16px',
              flexWrap: 'wrap',
              '& > div': {
                display: 'flex',
                flexDirection: 'row',
                gap: '16px'
              },
              '& .MuiFormGroup-root': {
                flexDirection: 'row !important',
                gap: '16px'
              },
              '& .MuiFormControl-root': {
                flexDirection: 'row !important'
              },
              '& > *': {
                display: 'flex',
                flexDirection: 'row !important',
                gap: '16px'
              }
            }}>
              <Checkboxes
                name='materialReceivingArea'
                readOnly={readOnly}
                value={materialReceivingArea || []}
                error={getError('data.appForm.examSecurity.materialReceivingArea')}
                options={RECEIVING_AREA_OPTIONS}
                onChange={(values: string[]) => {
                  const updates: Partial<IExamSecurity> = { materialReceivingArea: values };
                  // Clear "Other" specification if "Other" is not selected
                  if (!values.includes('Other')) {
                    updates.materialReceivingAreaOther = undefined;
                  }
                  changed(updates);
                }}
              />
            </Box>
          </FieldRow>

          {/* Conditional "Other" specification */}
          {materialReceivingArea?.includes('Other') && (
            <FieldRow>
              <FieldLabel variant="body1">
                Please specify the receiving location
              </FieldLabel>
              <Input
                disabled={readOnly}
                value={materialReceivingAreaOther || ''}
                validation={hasError('data.appForm.examSecurity.materialReceivingAreaOther') ? 'error' : undefined}
                validationMsg={getError('data.appForm.examSecurity.materialReceivingAreaOther')}
                onChange={(e) => {
                  changed({ materialReceivingAreaOther: e.target.value });
                }}
                placeholder="Specify other location"
                maxLength={255}

              />
            </FieldRow>
          )}

          {/* Where will materials be stored? */}
          <FieldRow>
            <FieldLabel variant="body1">
              Where will College Board materials be stored?
            </FieldLabel>
            <Input
              disabled={readOnly}
              value={materialStorageLocation || ''}
              validation={hasError('data.appForm.examSecurity.materialStorageLocation') ? 'error' : undefined}
              validationMsg={getError('data.appForm.examSecurity.materialStorageLocation')}
              onChange={(e) => {
                changed({ materialStorageLocation: e.target.value });
              }}
              placeholder="Storage location"
              maxLength={255}
            />
          </FieldRow>

          {/* Can storage area be locked? */}
          <FieldRow>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={8}>
                <FieldLabel variant="body1">
                  Can the exam material storage area be locked?
                </FieldLabel>
              </Grid>
              <Grid item xs={4}>
                <BooleanRadio
                  readOnly={readOnly}
                  error={getError('data.appForm.examSecurity.isMaterialStorageLocked')}
                  value={isMaterialStorageLocked}
                  name='isMaterialStorageLocked'
                  onChange={(value) => {
                    changed({ isMaterialStorageLocked: value });
                  }}
                />
              </Grid>
            </Grid>
          </FieldRow>

          {/* Security responsible person */}
          <FieldRow>
            <FieldLabel variant="body1">
              Please indicate the individual that will be responsible for College Board exam material security:
            </FieldLabel>
            <Input
              disabled={readOnly}
              value={securityResponsible || ''}
              validation={hasError('data.appForm.examSecurity.securityResponsible') ? 'error' : undefined}
              validationMsg={getError('data.appForm.examSecurity.securityResponsible')}
              onChange={(e) => {
                changed({ securityResponsible: e.target.value });
              }}
              placeholder="Enter name and title"
              maxLength={255}
            />
          </FieldRow>

          {/* Non-employed access */}
          <FieldRow>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={8}>
                <FieldLabel variant="body1">
                  Can any non-employed persons obtain access to secure material storage?
                </FieldLabel>
              </Grid>
              <Grid item xs={4}>
                <BooleanRadio
                  readOnly={readOnly}
                  error={getError('data.appForm.examSecurity.allowNonEmployedAccess')}
                  value={allowNonEmployedAccess}
                  name='allowNonEmployedAccess'
                  onChange={(value) => {
                    changed({ allowNonEmployedAccess: value });
                  }}
                />
              </Grid>
            </Grid>
          </FieldRow>

          {/* Testing rooms available */}
          <FieldRow>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={8}>
                <FieldLabel variant="body1">
                  Does your organization have rooms available to serve as testing spaces during scheduled exam periods?
                </FieldLabel>
              </Grid>
              <Grid item xs={4}>
                <BooleanRadio
                  readOnly={readOnly}
                  error={getError('data.appForm.examSecurity.hasDesignatedTestingRooms')}
                  value={hasDesignatedTestingRooms}
                  name='hasDesignatedTestingRooms'
                  onChange={(value) => {
                    const updates: Partial<IExamSecurity> = { hasDesignatedTestingRooms: value };
                    // Clear power/wifi question if No is selected
                    if (!value) {
                      updates.hasTestingRoomPowerWifi = undefined;
                    }
                    changed(updates);
                  }}
                />
              </Grid>
            </Grid>
          </FieldRow>

          {/* Conditional power and wifi */}
          {hasDesignatedTestingRooms && (
            <FieldRow>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={8}>
                  <FieldLabel variant="body1">
                    Will designated testing spaces have power and WIFI access?
                  </FieldLabel>
                </Grid>
                <Grid item xs={4}>
                  <BooleanRadio
                    readOnly={readOnly}
                    error={getError('data.appForm.examSecurity.hasTestingRoomPowerWifi')}
                    value={hasTestingRoomPowerWifi}
                    name='hasTestingRoomPowerWifi'
                    onChange={(value) => {
                      changed({ hasTestingRoomPowerWifi: value });
                    }}
                  />
                </Grid>
              </Grid>
            </FieldRow>
          )}
        </Grid>
      </Grid>
    </Panel>
  );
};